import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import moment from 'moment';
import { Helpers } from '../../helpers/Helpers';
import { MenuComponent } from '../../_metronic/assets/ts/components';
import { KTCard } from '../../_metronic/helpers/components/KTCard';
import { KTCardBody } from '../../_metronic/helpers/components/KTCardBody';
import Content from '../../_metronic/layout/components/content/Content';
import { PageTitle } from '../../_metronic/layout/core';
import { ToolbarWrapper } from '../../_metronic/layout/components/toolbar';
import { storeActivityIndicator, storeEbbeModalCallback } from '../../redux/reducers/authSlice';
import { RootState } from '../../redux/store';
import TableFooter from '../../components/TableFooter';
import RefreshButton from '../../components/RefreshButton';
import ColumnChooser from '../../components/ColumnChooser';
import ExcelExportMenu from '../../components/ExcelExportMenu';
import TableConstantActions from '../../components/TableConstantActions';
import { ColumnChooserModel } from '../../models/common/ColumnChooserModel';
import { ColumnFilterModel } from '../../models/common/ColumnFilterModel';
import { kolonlariBirlestir } from '../../components/ColumnFilter/kolonBirlestir';
import ColumnFilterRow from '../../components/ColumnFilter/ColumnFilterRow';
import ColumnFilterChips from '../../components/ColumnFilter/ColumnFilterChips';
import { ColumnFilterDefinitions } from '../../components/ColumnFilter/ColumnFilterTypes';
import { useSortableColumns } from '../../helpers/useSortableColumns';
import { ExcelExportMode } from '../../helpers/excelExportRequest';
import { ekrandaAra, ekrandaSayfala, ekrandaSirala, ekrandaSuz } from '../../helpers/ekrandaListe';
import { talepDolulugu, talepDurumAdi, talepDurumRengi } from '../../helpers/talepDoluluk';
import { davetDurumu, davetOzeti } from '../../helpers/mesaiDavet';
import { xlsxIndir, XlsxHucre } from '../../helpers/xlsxYaz';
import { trTarih } from '../../services/MesaiKaydiService';
import {
    MesaiTalebiItem, MesaiTalebiKaydetIstegi, MesaiTalebiSecenekleri, MesaiTalebiService, TALEP_DURUMLARI, TalepDurumu
} from '../../services/MesaiTalebiService';
import MesaiTalebiModal from './Components/MesaiTalebiModal';

/** Liste tek seferde gelir; arama, kolon filtresi, sıralama ve sayfalama ekranda uygulanır. */
interface EkranFiltresi {
    searchTerm: string
    skip: number
    take: number
    currentPage: number
    sortColumn?: string
    sortDirection?: string
    columnFilters: ColumnFilterModel[]
    /** Sunucu süzgeci: PLAN (bugün ve sonrası), TAMAM (geçmiş) ya da boş = tümü. */
    durum: TalepDurumu | ''
    baslangic: string
    bitis: string
}

const ilkFiltre: EkranFiltresi = {
    searchTerm: '',
    skip: 0,
    take: 50,
    currentPage: 1,
    sortColumn: undefined,
    sortDirection: undefined,
    columnFilters: [],
    durum: 'PLAN',
    baslangic: '',
    bitis: '',
};

const bosSecenekler: MesaiTalebiSecenekleri = { depolar: [], poSatirlari: [], araliklar: [], personeller: [] };

const durumKodu = (x: MesaiTalebiItem) => x.durum;

const yerlesenlerMetni = (x: MesaiTalebiItem) => (x.yerlesenler ?? []).map(y => y.ad + (y.faturaAraligi ? ' ' + y.faturaAraligi : '')).join(', ');

/** Süzme / sıralama / arama için satırın kolon değeri (seçimli kolonda filtre değeriyle aynı). */
const kolonDegeri = (x: MesaiTalebiItem, kolon: string): string | number | null | undefined => {
    switch (kolon) {
        case 'tarih': return x.tarih ? x.tarih.substring(0, 10) : '';
        case 'aralik': return x.aralik || (x.baslangic + '–' + x.bitis);
        case 'kisiSayisi': return x.kisiSayisi;
        case 'yerlesenSayisi': return x.yerlesenSayisi;
        case 'eksik': return x.eksik ?? talepDolulugu(x).eksik;
        case 'yerlesenler': return yerlesenlerMetni(x);
        case 'davetler': return davetOzeti(x.ayrilanlar, x.reddedenler);
        case 'siparis': return x.siparis?.refNo ?? '';
        case 'tutar': return x.tutar;
        case 'durum': return durumKodu(x);
        case 'giren': return x.giren ?? '';
        default: return (x as any)[kolon];
    }
}

/** Excel hücresi: ekranda görünen metin (sayılar sayı olarak). */
const excelDegeri = (x: MesaiTalebiItem, kolon: string): XlsxHucre => {
    switch (kolon) {
        case 'tarih': return x.tarihStr || trTarih(x.tarih);
        case 'durum': return talepDurumAdi(x.durum) + (x.faturali ? ' · Faturalı' : '');
        case 'giren': return (x.giren ?? '') + (x.createdDate ? ' - ' + x.createdDate : '');
        case 'tutar': return x.tutar ?? null;
        default: {
            var v = kolonDegeri(x, kolon);
            return v == null ? null : v;
        }
    }
}

const ARAMA_KOLONLARI = ['tarih', 'depoAdi', 'aralik', 'customerName', 'divisionName', 'poName', 'poNo', 'yerlesenler', 'davetler', 'siparis', 'giren'];

const TON_SINIFI = { basari: 'badge-light-success', uyari: 'badge-light-warning', tehlike: 'badge-light-danger' };

const KUTU_RENGI = { yerlesen: '#1b84ff', ayrilan: '#7239EA', bos: 'transparent' };

/**
 * Ek Mesai Talepleri (Kargo Modülü > Nakliye, QR Mesai Faz 2, 18.10.2026; standart liste ekranı).
 * Durum süzgeci varsayılan "Planlanan" (bugün ve sonrası). Ekle / Güncelle penceresi, Sil (onaylı), Excel.
 * Sipariş numarasına tıklayınca sipariş ekranı açılır.
 */
const MesaiTalebiList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const auth = useSelector((state: RootState) => state.auth)
    const pageName = "Kargo Modülü > Nakliye > Ek Mesai Talepleri"
    const pageVersion = "1";
    const menu = auth.user.user?.data.authorizedMenus.find(x => x.menuName == pageName);

    const [liste, setListe] = useState<MesaiTalebiItem[]>([]);
    const [filter, setFilter] = useState<EkranFiltresi>(ilkFiltre);
    const [secenekler, setSecenekler] = useState<MesaiTalebiSecenekleri>(bosSecenekler);
    const [pencereAcik, setPencereAcik] = useState(false);
    const [duzenlenen, setDuzenlenen] = useState<MesaiTalebiItem | undefined>(undefined);
    /** Zilden gelince yalnız o talepler listelenir (#514). */
    const [yalnizTalepler, setYalnizTalepler] = useState<string[] | undefined>(undefined);
    const [zildenAcilacak, setZildenAcilacak] = useState<string | undefined>(undefined);

    const getColumns = () => {
        var str = localStorage.getItem(pageName + "-" + pageVersion + "-filters");

        var list: ColumnChooserModel[] = [
            { title: 'Tarih', name: 'tarih', order: 0, isVisible: true },
            { title: 'Depo', name: 'depoAdi', order: 0, isVisible: true },
            { title: 'Saat Aralığı', name: 'aralik', order: 0, isVisible: true },
            { title: 'Müşteri', name: 'customerName', order: 0, isVisible: true },
            { title: 'Bölüm', name: 'divisionName', order: 0, isVisible: true },
            { title: 'PO', name: 'poName', order: 0, isVisible: true },
            { title: 'PO No', name: 'poNo', order: 0, isVisible: true },
            { title: 'İstenen', name: 'kisiSayisi', order: 0, isVisible: true, className: 'custom-text-right' },
            { title: 'Yerleşen', name: 'yerlesenSayisi', order: 0, isVisible: true, className: 'custom-text-right' },
            { title: 'Eksik', name: 'eksik', order: 0, isVisible: true },
            { title: 'Yerleşenler', name: 'yerlesenler', order: 0, isVisible: true },
            { title: 'Davetler', name: 'davetler', order: 0, isVisible: true },
            { title: 'Sipariş', name: 'siparis', order: 0, isVisible: true },
            { title: 'Tutar', name: 'tutar', order: 0, isVisible: true, className: 'custom-text-right' },
            { title: 'Durum', name: 'durum', order: 0, isVisible: true },
            { title: 'Giren', name: 'giren', order: 0, isVisible: true },
        ];

        for (var i = 0; i < list.length; i++)
            list[i].order = i + 1;

        return kolonlariBirlestir(list, str);
    }

    const [columns, setColumns] = useState<ColumnChooserModel[]>(getColumns());

    useEffect(() => {
        setTimeout(() => { MenuComponent.reinitialization() }, 1000)

        // Zilden gelen talep geçmiş tarihli olabilir: durum süzgeci açılmadan listelenir.
        getData((location.state as any)?.talepIdleri?.length > 0 ? { ...filter, durum: '' } : undefined);
        getSecenekler();
    }, [])

    // Zildeki ek mesai satırı: tek talepse güncelleme penceresi açılır, birden fazlaysa yalnız o talepler listelenir.
    useEffect(() => {
        const idler: string[] | undefined = (location.state as any)?.talepIdleri;
        if (!idler || idler.length == 0)
            return;

        setYalnizTalepler(idler);
        setZildenAcilacak(idler.length == 1 ? idler[0] : undefined);

        // Aynı sayfadayken zile tıklanırsa yeni sayılar sunucudan alınır.
        if (liste.length > 0)
            getData({ ...filter, durum: '', currentPage: 1, skip: 0 });
    }, [location.key])

    useEffect(() => {
        if (!zildenAcilacak || liste.length == 0)
            return;

        const x = liste.find(y => y.mesaiTalebiId == zildenAcilacak);

        if (x && !x.faturali) {
            setDuzenlenen(x);
            setPencereAcik(true);
        }

        setZildenAcilacak(undefined);
    }, [zildenAcilacak, liste])

    // ---------------------------------------------------------------- Veri

    // Süzgeç art arda değişebilir; yalnızca en son isteğin cevabı ekrana yazılır.
    const lastRequestNo = useRef(0);

    const getData = async (_filter: EkranFiltresi | undefined = undefined) => {
        const requestNo = ++lastRequestNo.current;

        try {
            if (_filter)
                setFilter(_filter);
            else
                _filter = filter;

            dispatch(storeActivityIndicator(true))

            var response = await MesaiTalebiService.liste({
                durum: _filter.durum || null,
                baslangic: _filter.baslangic || null,
                bitis: _filter.bitis || null,
            });

            if (requestNo != lastRequestNo.current)
                return;

            if (!response.isSuccess) {
                toast.error(response.message ?? '')
                return;
            }

            setListe((response.data ?? []).map(x => ({ ...x, isSelected: false })));
        } catch (error: any) {
            toast.error(error)
        }
        finally {
            if (requestNo == lastRequestNo.current)
                dispatch(storeActivityIndicator(false))
        }
    }

    const getSecenekler = async () => {
        try {
            var response = await MesaiTalebiService.secenekler();

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            setSecenekler({ ...bosSecenekler, ...(response.data ?? {}) });
        } catch (error: any) {
            toast.error(error);
        }
    }

    const sunucuSuzgeciDegisti = (degisim: Partial<EkranFiltresi>) => {
        var yeni = { ...filter, ...degisim, currentPage: 1, skip: 0 };

        if (yeni.baslangic && yeni.bitis && yeni.baslangic > yeni.bitis) {
            toast.error('Başlangıç tarihi bitişten sonra olamaz.');
            return;
        }

        getData(yeni);
    }

    // ---------------------------------------------------------------- Ekranda arama / süzme / sıralama / sayfalama

    const gosterilen = yalnizTalepler ? liste.filter(x => yalnizTalepler.includes(x.mesaiTalebiId)) : liste;

    const suzulen = ekrandaSirala(
        ekrandaSuz(ekrandaAra(gosterilen, filter.searchTerm, ARAMA_KOLONLARI, kolonDegeri), filter.columnFilters, kolonDegeri),
        filter.sortColumn, filter.sortDirection, kolonDegeri
    );

    const sayfa = ekrandaSayfala(suzulen, filter.skip, filter.take);

    const performPagination = (i: number) => {
        setFilter({ ...filter, currentPage: i, skip: (i - 1) * filter.take });
    }

    const changeTake = (_take: number) => {
        setFilter({ ...filter, currentPage: 1, skip: 0, take: _take });
    }

    const sortableColumns = [
        'tarih', 'depoAdi', 'aralik', 'customerName', 'divisionName', 'poName', 'poNo', 'kisiSayisi', 'yerlesenSayisi', 'eksik', 'davetler', 'siparis', 'tutar', 'durum', 'giren'
    ];

    const { thProps, sortArrow } = useSortableColumns(sortableColumns, filter, (f?: EkranFiltresi) => { if (f) setFilter(f); });

    // Saat aralığı seçenekleri ekrandaki listeden (Çalışma Saatleri'ndeki liste zamanla değişebilir).
    const aralikSecenekleri = () => Promise.resolve(
        Array.from(new Set(liste.map(x => String(kolonDegeri(x, 'aralik') ?? '')).filter(x => x)))
            .sort((a, b) => a.localeCompare(b, 'tr'))
            .map(x => ({ value: x, label: x }))
    );

    const columnFilterDefinitions: ColumnFilterDefinitions = {
        tarih: { type: 'date' },
        depoAdi: { type: 'text' },
        aralik: { type: 'select', allowEmpty: false, loadOptions: aralikSecenekleri },
        customerName: { type: 'text' },
        divisionName: { type: 'text' },
        poName: { type: 'text' },
        poNo: { type: 'text' },
        kisiSayisi: { type: 'number' },
        yerlesenSayisi: { type: 'number' },
        eksik: { type: 'number' },
        yerlesenler: { type: 'text' },
        davetler: { type: 'text' },
        siparis: { type: 'text' },
        tutar: { type: 'number' },
        durum: { type: 'select', allowEmpty: false, loadOptions: () => Promise.resolve(TALEP_DURUMLARI.map(x => ({ value: x.value, label: x.label }))) },
        giren: { type: 'text' },
    };

    const changeColumnFilters = (columnFilters: ColumnFilterModel[]) => {
        setFilter({ ...filter, columnFilters: columnFilters, currentPage: 1, skip: 0 });
    }

    // ---------------------------------------------------------------- Seçim

    const secililer = liste.filter(x => x.isSelected);

    const setItemSelected = (_id: string) => {
        setListe(liste.map(x => x.mesaiTalebiId == _id ? { ...x, isSelected: !x.isSelected } : x));
    }

    const selectAll = () => {
        var ids = sayfa.map(x => x.mesaiTalebiId);
        var isAllSelected = sayfa.length > 0 && sayfa.every(x => x.isSelected);

        setListe(liste.map(x => ids.includes(x.mesaiTalebiId) ? { ...x, isSelected: !isAllSelected } : x));
    }

    // ---------------------------------------------------------------- Ekle / Güncelle / Sil

    const yeniEkle = () => {
        setDuzenlenen(undefined);
        setPencereAcik(true);
    }

    const guncelle = (_id: string | undefined = undefined) => {
        var x = _id ? liste.find(y => y.mesaiTalebiId == _id) : secililer[0];

        if (!x)
            return;

        if (x.faturali) {
            toast.error('Faturalanmış talep güncellenemez.');
            return;
        }

        setDuzenlenen(x);
        setPencereAcik(true);
    }

    const kaydet = async (istek: MesaiTalebiKaydetIstegi) => {
        try {
            dispatch(storeActivityIndicator(true))

            var response = await MesaiTalebiService.kaydet(istek);

            if (!response.isSuccess) {
                toast.error(response.message ?? '')
                dispatch(storeActivityIndicator(false))
                return;
            }

            toast.success(istek.mesaiTalebiId ? 'Talep güncellendi; yerleşim yeniden hesaplandı.' : 'Talep kaydedildi.')

            setPencereAcik(false);
            setDuzenlenen(undefined);

            // Kaydedilen talep seçili durumun dışında kalıyorsa (ör. geçmiş güne giriş) süzgeç "Tümü"ne alınır.
            var gecmis = istek.tarih < moment().format('YYYY-MM-DD');
            var yeni = { ...filter };

            if ((filter.durum == 'PLAN' && gecmis) || (!!filter.durum && filter.durum != 'PLAN'))
                yeni.durum = '';

            getData(yeni);
        } catch (error: any) {
            toast.error(error)
            dispatch(storeActivityIndicator(false))
        }
    }

    const sil = async () => {
        try {
            dispatch(storeActivityIndicator(true))

            var response = await MesaiTalebiService.sil(secililer.map(x => x.mesaiTalebiId));

            if (!response.isSuccess) {
                toast.error(response.message ?? '')
                dispatch(storeActivityIndicator(false))
                return;
            }

            toast.success('Seçilen talepler silindi.')

            getData();
        } catch (error: any) {
            toast.error(error)
            dispatch(storeActivityIndicator(false))
        }
    }

    const silOnayi = () => {
        if (secililer.length == 0)
            return;

        if (secililer.some(x => x.faturali)) {
            toast.error('Faturalanmış talep silinemez: ' + secililer.filter(x => x.faturali).map(x => x.siparis?.refNo ?? x.tarihStr).join(', '));
            return;
        }

        var siparisli = secililer.filter(x => x.siparis);

        dispatch(storeEbbeModalCallback({
            title: 'Ek mesai talebini sil',
            body: '',
            vurgu: siparisli.length > 0
                ? {
                    renk: 'kirmizi',
                    baslik: 'Siparişten satırlar çıkarılır',
                    kalin: siparisli.map(x => x.siparis!.refNo).join(', '),
                    metin: 'siparişindeki bu talebe ait mesai satırları ve beklenen gelir silinir; yerleşen kayıtlar başka talebe ya da Atanacak Mesailer\'e düşer.',
                    soru: `${secililer.length} talep silinsin mi?`
                }
                : {
                    renk: 'turuncu',
                    baslik: 'Talep silinecek',
                    kalin: secililer.length + ' talep',
                    metin: 'silinecek; isimle ayrılan personel serbest kalır.',
                    soru: 'Devam edilsin mi?'
                },
            buttons: [
                { text: 'Vazgeç', btnClass: 'btn-light' },
                { text: 'Sil', btnClass: 'btn-danger', onClick: sil }
            ]
        }))
    }

    // ---------------------------------------------------------------- Excel (ekranda üretilir)

    const excelExport = (mode: ExcelExportMode) => {
        var satirlar = mode == 'page' ? sayfa : mode == 'selected' ? ekrandaSirala(secililer, filter.sortColumn, filter.sortDirection, kolonDegeri) : suzulen;

        if (satirlar.length == 0) {
            toast.error('Aktarılacak kayıt yok.');
            return;
        }

        var kolonlar = columns.filter(x => x.isVisible).sort((a, b) => a.order - b.order);

        try {
            xlsxIndir(
                'Ek Mesai Talepleri-' + moment().format('DD_MM_YYYY_HH_mm'),
                'Ek Mesai Talepleri',
                kolonlar.map(k => k.title),
                satirlar.map(x => kolonlar.map(k => excelDegeri(x, k.name)))
            );
        } catch (error: any) {
            toast.error('Excel oluşturulamadı.');
        }
    }

    // ---------------------------------------------------------------- Hücreler

    const dolulukHucresi = (x: MesaiTalebiItem) => {
        var d = talepDolulugu(x, x.tamamlandi);

        return (
            <div className='d-flex align-items-center flex-nowrap'>
                <span className='d-inline-flex me-2' title='■ yerleşen · mor: isimle ayrılan · □ boş'>
                    {
                        d.kutular.slice(0, 20).map((k, i) =>
                            <i key={i} style={{ display: 'inline-block', width: 10, height: 10, marginRight: 2, borderRadius: 2, background: KUTU_RENGI[k], border: '1.5px solid ' + (k == 'bos' ? '#C4CADA' : KUTU_RENGI[k]) }} />
                        )
                    }
                </span>
                <span className={'badge ebbe-status-badge ' + TON_SINIFI[d.rozet.ton]}>{d.rozet.metin}</span>
            </div>
        );
    }

    const yerlesenlerHucresi = (x: MesaiTalebiItem) => {
        var bekleyen = (x.ayrilanlar ?? []).filter(a => !a.geldi);

        if ((x.yerlesenler ?? []).length == 0 && bekleyen.length == 0)
            return <span className='text-muted'>—</span>;

        return (
            <div style={{ whiteSpace: 'normal', minWidth: 160 }}>
                {
                    (x.yerlesenler ?? []).map(y =>
                        <div key={y.mesaiKaydiId}>{y.ad} <span className='text-muted'>{y.faturaAraligi}</span></div>
                    )
                }
                {
                    bekleyen.length > 0 && (
                        <div style={{ color: '#7239EA' }}>Ayrılan: {bekleyen.map(a => a.ad).join(', ')}</div>
                    )
                }
            </div>
        );
    }

    const davetlerHucresi = (x: MesaiTalebiItem) => {
        var satirlar = [...(x.ayrilanlar ?? []), ...(x.reddedenler ?? [])].filter(a => a.davetDurumu != null);

        if (satirlar.length == 0)
            return <span className='text-muted'>—</span>;

        return (
            <div style={{ whiteSpace: 'normal', minWidth: 170 }}>
                {
                    satirlar.map(a => {
                        var d = davetDurumu(a.davetDurumu);
                        return (
                            <div key={a.personnelId} className='mb-1' title={a.redNotu ?? undefined}>
                                <span className={'badge ebbe-status-badge me-1 ' + d.sinif}>{d.metin}</span>
                                {a.ad}
                                {a.yanitSuresi ? <span className='text-muted'> · {a.yanitSuresi}</span> : null}
                            </div>
                        );
                    })
                }
            </div>
        );
    }

    const siparisHucresi = (x: MesaiTalebiItem) => {
        if (x.siparis)
            return (
                <a className='btn btn-sm btn-light-primary py-1 px-2' title='Siparişi aç' onClick={(e) => { e.stopPropagation(); navigate('/ambar/listadd?id=' + x.siparis!.ambarId); }}>
                    {x.siparis.refNo}
                </a>
            );

        return <span className='text-muted fs-7'>{x.tamamlandi ? 'Sipariş oluşmadı' : 'İlk kişiyle açılır'}</span>;
    }

    const getColumnValue = (item: MesaiTalebiItem, column: string, className: string | undefined) => {
        var icerik: React.ReactNode;

        switch (column) {
            case 'tarih': icerik = item.tarihStr || trTarih(item.tarih); break;
            case 'aralik': icerik = <span className='badge badge-light-primary ebbe-status-badge'>{kolonDegeri(item, 'aralik')}</span>; break;
            case 'eksik': icerik = dolulukHucresi(item); break;
            case 'yerlesenler': icerik = yerlesenlerHucresi(item); break;
            case 'davetler': icerik = davetlerHucresi(item); break;
            case 'siparis': icerik = siparisHucresi(item); break;
            case 'tutar': icerik = item.tutar ? Helpers.correctPrice(item.tutar, 2) : ''; break;
            case 'durum':
                icerik = (
                    <React.Fragment>
                        <span className={'badge ebbe-status-badge ' + talepDurumRengi(item.durum)}>{talepDurumAdi(item.durum)}</span>
                        {item.faturali && <span className='badge badge-light-warning ebbe-status-badge ms-1'>Faturalı</span>}
                    </React.Fragment>
                );
                break;
            case 'giren': icerik = (item.giren ?? '') + (item.createdDate ? ' - ' + item.createdDate : ''); break;
            default: icerik = (item as any)[column]; break;
        }

        return <td key={column} className={className} onClick={() => setItemSelected(item.mesaiTalebiId)} onDoubleClick={() => menu?.canUpdate && guncelle(item.mesaiTalebiId)}>{icerik}</td>;
    }

    const gorunenKolonlar = columns.filter(x => x.isVisible).sort((a, b) => a.order - b.order);

    return (
        <React.Fragment>
            <ToolbarWrapper filters={<React.Fragment>
                <RefreshButton onClick={() => { getData(); getSecenekler(); }} />

                {
                    yalnizTalepler && (
                        <button type='button' className='btn btn-sm btn-light-danger' title='Bütün talepleri göster' onClick={() => setYalnizTalepler(undefined)}>
                            Yalnız davet bekleyen {yalnizTalepler.length} talep <i className='fa fa-times ms-1' />
                        </button>
                    )
                }

                <div className='m-0 d-flex align-items-center'>
                    <select
                        className='form-select form-select-sm form-select-solid me-2'
                        style={{ width: 140 }}
                        title='Durum'
                        value={filter.durum}
                        onChange={(e) => sunucuSuzgeciDegisti({ durum: e.target.value as TalepDurumu | '' })}
                    >
                        {TALEP_DURUMLARI.map(x => <option key={x.value} value={x.value}>{x.label}</option>)}
                        <option value=''>Tümü</option>
                    </select>

                    <input
                        type='date'
                        title='Başlangıç (boş: sınırsız)'
                        className='form-control form-control-sm form-control-solid'
                        style={{ width: 140 }}
                        value={filter.baslangic}
                        onChange={(e) => sunucuSuzgeciDegisti({ baslangic: e.target.value })}
                    />
                    <span className='mx-1 text-muted'>–</span>
                    <input
                        type='date'
                        title='Bitiş (boş: sınırsız)'
                        className='form-control form-control-sm form-control-solid'
                        style={{ width: 140 }}
                        value={filter.bitis}
                        onChange={(e) => sunucuSuzgeciDegisti({ bitis: e.target.value })}
                    />
                </div>

                <TableConstantActions
                    onSearchSubmit={(e) => { e.preventDefault(); setFilter({ ...filter, currentPage: 1, skip: 0 }); }}
                    pageName={pageName}
                    searchTerm={filter.searchTerm}
                    selectedCount={secililer.length}
                    setSearchTerm={(e) => setFilter({ ...filter, searchTerm: e, currentPage: 1, skip: 0 })}
                    updateFunction={() => guncelle()}
                />

                {
                    menu?.canDelete && (
                        <button disabled={secililer.length == 0} onClick={silOnayi} className='btn btn-sm btn-flex btn-danger'>
                            <i className='fa fa-trash fs-6 color-gray-700 me-1' />
                            Sil
                        </button>
                    )
                }

                <ColumnChooser
                    columns={columns}
                    getColumns={getColumns}
                    pageName={pageName}
                    pageVersion={pageVersion}
                    setColumns={setColumns}
                />

                <ExcelExportMenu
                    pageName={pageName}
                    selectedCount={secililer.length}
                    onExport={excelExport}
                />

                {
                    menu?.canInsert && (
                        <div className='m-0'>
                            <a onClick={yeniEkle} className='btn btn-sm fw-bold btn-primary'>
                                Yeni Talep Ekle
                            </a>
                        </div>
                    )
                }
            </React.Fragment>} />

            <PageTitle breadcrumbs={[{
                title: pageName.split('>').slice(0, pageName.split('>').length - 1).join(' > ') + ' > ',
                isActive: false,
                path: ''
            }]}>{(pageName.split('>')[pageName.split('>').length - 1])}</PageTitle>

            <Content>
                <KTCard className='mb-3'>
                    <KTCardBody className='py-2'>
                        <ColumnFilterChips
                            columns={columns}
                            definitions={columnFilterDefinitions}
                            filters={filter.columnFilters}
                            onChange={changeColumnFilters}
                        />

                        <div className='table-responsive new-table-container'>
                            <table id='kt_table_users' className='table align-middle table-row-dashed dataTable no-footer ebbe-compact-table'>
                                <thead>
                                    <tr className='text-start text-muted fw-bolder text-uppercase'>
                                        <th style={{ width: 1 }}>
                                            <div className='d-flex'>
                                                <label className='form-check form-check-sm form-check-custom form-check-solid me-5'>
                                                    <input onChange={selectAll} className='form-check-input' type='checkbox' checked={sayfa.length > 0 && sayfa.every(x => x.isSelected)} />
                                                </label>
                                            </div>
                                        </th>

                                        {
                                            gorunenKolonlar.map(item =>
                                                <th key={item.name} {...thProps(item.name)}>
                                                    {item.title}
                                                    {sortArrow(item.name)}
                                                </th>
                                            )
                                        }
                                    </tr>

                                    <ColumnFilterRow
                                        columns={gorunenKolonlar}
                                        definitions={columnFilterDefinitions}
                                        filters={filter.columnFilters}
                                        onChange={changeColumnFilters}
                                        leadingCellCount={1}
                                    />
                                </thead>
                                <tbody className='text-gray-600 fw-bold'>
                                    {
                                        sayfa.length == 0 && (
                                            <tr>
                                                <td className="text-center" colSpan={99}>Kayıt bulunamadı.</td>
                                            </tr>
                                        )
                                    }
                                    {
                                        sayfa.map(item =>
                                            <tr key={item.mesaiTalebiId} style={{ background: item.isSelected ? 'rgba(204, 204, 204, 0.25)' : undefined }}>
                                                <td style={{ width: 1 }}>
                                                    <div className='d-flex'>
                                                        <label className='form-check form-check-sm form-check-custom form-check-solid me-5'>
                                                            <input onChange={() => setItemSelected(item.mesaiTalebiId)} className='form-check-input' type='checkbox' checked={item.isSelected ?? false} />
                                                        </label>
                                                    </div>
                                                </td>

                                                {gorunenKolonlar.map(c => getColumnValue(item, c.name, c.className))}
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                        </div>

                        <div className="text-muted fs-7 py-3">
                            Lojistik QR'ıyla yapılan mesai talebin günü, deposu ve saat aralığıyla kesişirse kapandığı sırayla yerleşir; faturalanan, aralığa düşen ek mesaidir (15 dakikaya aşağı yuvarlanır).
                            {' '}<span style={{ color: KUTU_RENGI.yerlesen }}>■</span> yerleşen · <span style={{ color: KUTU_RENGI.ayrilan }}>■</span> isimle ayrılmış · □ boş. Tutar KDV hariç beklenen gelirdir; faturalanmış talep değiştirilemez.
                        </div>
                    </KTCardBody>

                    <TableFooter
                        changeTake={changeTake}
                        take={filter.take}
                        currentPage={filter.currentPage}
                        performPagination={performPagination}
                        totalPageCount={Math.ceil(suzulen.length / filter.take)}
                        totalRecordCount={suzulen.length}
                        selectedCount={secililer.length}
                    />
                </KTCard>
            </Content>

            <MesaiTalebiModal
                isShown={pencereAcik}
                kayit={duzenlenen}
                secenekler={secenekler}
                onConfirm={kaydet}
                onClose={() => { setPencereAcik(false); setDuzenlenen(undefined); }}
            />
        </React.Fragment>
    )
}

export default MesaiTalebiList;
